import { createFileRoute, Link } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useChannels, useGuildConfig } from '../../../lib/auditQueries.js';
import { useRoles } from '../../../lib/automodQueries.js';
import { Breadcrumbs } from '../../../components/Breadcrumbs.js';
import { LanguageSwitcher } from '../../../components/LanguageSwitcher.js';
import { ChannelPicker } from '../../../components/ChannelPicker.js';
import {
  useCreateReactionRole,
  useDeleteReactionRole,
  useGuildEmojis,
  useHealthCheckReactionRole,
  usePublishReactionRole,
  useReactionRoles,
  useUpdateReactionRole,
  type CustomEmoji,
  type HealthCheckResult,
  type ReactionRoleCreateInput,
  type ReactionRoleMapping,
  type ReactionRoleMessage,
  type ReactionRolePatchInput,
} from '../../../lib/reactionRolesQueries.js';
import { ApiError } from '../../../lib/api.js';

export const Route = createFileRoute('/guilds/$guildId/reaction-roles')({
  component: ReactionRolesPage,
});

type Status = 'draft' | 'published' | 'broken' | 'unknown';

function emptyDraft(): ReactionRoleCreateInput {
  return {
    channelId: '',
    title: '',
    body: '',
    useEmbed: true,
    embedColor: '#6366f1',
    exclusiveGroup: false,
    enabled: true,
    mappings: [],
  };
}

function fromMessage(m: ReactionRoleMessage): ReactionRoleCreateInput {
  return {
    channelId: m.channelId,
    title: m.title,
    body: m.body,
    useEmbed: m.useEmbed,
    embedColor: m.embedColor,
    exclusiveGroup: m.exclusiveGroup,
    enabled: m.enabled,
    mappings: m.mappings.map((mp) => ({ ...mp })),
  };
}

function deriveStatus(message: ReactionRoleMessage, health: HealthCheckResult | null): Status {
  if (!message.messageId) return 'draft';
  if (!health) return 'unknown';
  if (
    health.messageMissing ||
    health.botMissingManageRoles ||
    health.guildUnavailable ||
    health.mappings.some((m) => !m.ok)
  ) {
    return 'broken';
  }
  return 'published';
}

function ReactionRolesPage() {
  const { t } = useTranslation();
  const { guildId } = Route.useParams();
  const list = useReactionRoles(guildId);
  const [editing, setEditing] = useState<
    { kind: 'create' } | { kind: 'edit'; rr: ReactionRoleMessage } | null
  >(null);

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100">
      <Header guildId={guildId} />
      <main className="mx-auto max-w-6xl px-6 py-8">
        <section className="rounded-xl border border-zinc-800 bg-zinc-900/40">
          <header className="flex items-center justify-between border-b border-zinc-800 px-5 py-3">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-zinc-300">
              {t('reactionRoles.list')}
            </h2>
            <button
              type="button"
              onClick={() => {
                setEditing({ kind: 'create' });
              }}
              className="rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-500"
            >
              {t('reactionRoles.button.create')}
            </button>
          </header>

          {list.isLoading ? (
            <p className="px-5 py-6 text-sm text-zinc-500">{t('common.loading')}</p>
          ) : !list.data || list.data.length === 0 ? (
            <p className="px-5 py-6 text-sm text-zinc-500">{t('reactionRoles.empty')}</p>
          ) : (
            <ul className="divide-y divide-zinc-800">
              {list.data.map((m) => (
                <RrRow
                  key={m.id}
                  rr={m}
                  guildId={guildId}
                  onEdit={() => {
                    setEditing({ kind: 'edit', rr: m });
                  }}
                />
              ))}
            </ul>
          )}
        </section>
      </main>

      {editing ? (
        <RrEditor
          guildId={guildId}
          mode={editing}
          onClose={() => {
            setEditing(null);
          }}
        />
      ) : null}
    </div>
  );
}

function Header({ guildId }: { guildId: string }) {
  const { t } = useTranslation();
  const guildConfig = useGuildConfig(guildId);
  return (
    <header className="border-b border-zinc-800">
      <div className="mx-auto flex max-w-6xl items-center gap-3 px-6 py-4">
        <Link to="/" className="shrink-0">
          <img src="/icon.png" alt="Frankenstein" className="h-6 w-6" />
        </Link>
        <Breadcrumbs
          items={[
            { label: t('app.name'), to: '/' },
            {
              label: guildConfig.data?.name ?? guildId,
              to: '/guilds/$guildId',
              params: { guildId },
            },
            { label: t('reactionRoles.title') },
          ]}
        />
        <div className="ml-auto">
          <LanguageSwitcher />
        </div>
      </div>
    </header>
  );
}

function StatusBadge({ status, onClick }: { status: Status; onClick: (() => void) | undefined }) {
  const { t } = useTranslation();
  const cls =
    status === 'published'
      ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300'
      : status === 'draft'
        ? 'border-zinc-700 bg-zinc-800 text-zinc-300'
        : status === 'broken'
          ? 'border-rose-500/40 bg-rose-500/10 text-rose-300'
          : 'border-zinc-700 bg-zinc-800 text-zinc-400';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      className={`rounded-full border px-2 py-0.5 text-xs ${cls} ${onClick ? 'cursor-pointer' : 'cursor-default'}`}
    >
      {t(`reactionRoles.status.${status}`)}
    </button>
  );
}

function RrRow({
  rr,
  guildId,
  onEdit,
}: {
  rr: ReactionRoleMessage;
  guildId: string;
  onEdit: () => void;
}) {
  const { t } = useTranslation();
  const remove = useDeleteReactionRole(guildId);
  const health = useHealthCheckReactionRole(guildId);
  const [issuesOpen, setIssuesOpen] = useState(false);

  const status = deriveStatus(rr, health.data ?? null);

  return (
    <li className="flex items-center gap-3 px-5 py-3">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="truncate text-sm font-medium">{rr.title}</p>
          <StatusBadge
            status={status}
            onClick={
              status === 'broken'
                ? () => {
                    setIssuesOpen(true);
                  }
                : undefined
            }
          />
        </div>
        <p className="text-xs text-zinc-500">
          {t('reactionRoles.row.mappingCount', { count: rr.mappings.length })}
          {rr.exclusiveGroup ? ` · ${t('reactionRoles.row.exclusive')}` : ''}
        </p>
      </div>
      <button
        type="button"
        onClick={() => {
          health.mutate(rr.id);
        }}
        className="rounded-md border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-xs text-zinc-300 hover:bg-zinc-800"
      >
        {health.isPending ? t('common.loading') : t('reactionRoles.button.healthCheck')}
      </button>
      <button
        type="button"
        onClick={onEdit}
        className="rounded-md border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-xs text-zinc-300 hover:bg-zinc-800"
      >
        {t('reactionRoles.button.edit')}
      </button>
      <button
        type="button"
        onClick={() => {
          if (window.confirm(t('reactionRoles.deleteConfirm', { title: rr.title }))) {
            remove.mutate(rr.id);
          }
        }}
        className="rounded-md border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-xs text-rose-300 hover:bg-zinc-800"
      >
        {t('common.remove')}
      </button>

      {issuesOpen && health.data ? (
        <IssuesModal
          health={health.data}
          onClose={() => {
            setIssuesOpen(false);
          }}
        />
      ) : null}
    </li>
  );
}

function IssuesModal({ health, onClose }: { health: HealthCheckResult; onClose: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-lg rounded-xl border border-zinc-800 bg-zinc-950 p-5">
        <h2 className="mb-3 text-sm font-semibold">{t('reactionRoles.issues.title')}</h2>
        <ul className="space-y-2 text-sm">
          {health.messageMissing ? (
            <li className="text-rose-300">{t('reactionRoles.issues.messageMissing')}</li>
          ) : null}
          {health.botMissingManageRoles ? (
            <li className="text-rose-300">{t('reactionRoles.issues.botMissingManageRoles')}</li>
          ) : null}
          {health.guildUnavailable ? (
            <li className="text-rose-300">{t('reactionRoles.issues.guildUnavailable')}</li>
          ) : null}
          {health.mappings
            .filter((m) => !m.ok)
            .map((m) => (
              <li key={m.roleId} className="text-rose-300">
                <span className="text-zinc-500">{m.emoji} → </span>
                {m.issues.join(', ')}
              </li>
            ))}
          {!health.messageMissing &&
          !health.botMissingManageRoles &&
          !health.guildUnavailable &&
          health.mappings.every((m) => m.ok) ? (
            <li className="text-emerald-300">{t('reactionRoles.issues.allOk')}</li>
          ) : null}
        </ul>
        <div className="mt-4 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-xs text-zinc-300 hover:bg-zinc-800"
          >
            {t('common.dismiss')}
          </button>
        </div>
      </div>
    </div>
  );
}

interface ApiErrorBody {
  error?: string;
  message?: string;
  missingPermissions?: string[];
  issues?: { fieldErrors?: Record<string, string[]> };
}

function extractErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const body = err.body as ApiErrorBody | null;
    if (body?.message) return body.message;
    if (body?.missingPermissions?.length) return `Missing: ${body.missingPermissions.join(', ')}`;
    if (body?.issues?.fieldErrors) {
      const parts: string[] = [];
      for (const [k, v] of Object.entries(body.issues.fieldErrors)) {
        if (v && v.length > 0) parts.push(`${k}: ${v.join(', ')}`);
      }
      if (parts.length > 0) return parts.join(' · ');
    }
    if (body?.error) return body.error;
  }
  return err instanceof Error ? err.message : fallback;
}

function RrEditor({
  guildId,
  mode,
  onClose,
}: {
  guildId: string;
  mode: { kind: 'create' } | { kind: 'edit'; rr: ReactionRoleMessage };
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const create = useCreateReactionRole(guildId);
  const update = useUpdateReactionRole(guildId);
  const publish = usePublishReactionRole(guildId);
  const channels = useChannels(guildId);
  const roles = useRoles(guildId);
  const emojis = useGuildEmojis(guildId);

  const [draft, setDraft] = useState<ReactionRoleCreateInput>(
    mode.kind === 'edit' ? fromMessage(mode.rr) : emptyDraft(),
  );
  const channelOptions = useMemo(() => channels.data ?? [], [channels.data]);
  const roleOptions = useMemo(() => roles.data ?? [], [roles.data]);
  const emojiOptions = useMemo(() => emojis.data ?? [], [emojis.data]);

  const isPending = create.isPending || update.isPending || publish.isPending;
  const submitError = create.error ?? update.error;

  const onSubmit = (): void => {
    if (mode.kind === 'create') {
      create.mutate(draft, {
        onSuccess: (created) => {
          // Switch the editor over to the new id so Publish becomes available.
          setDraft(fromMessage(created));
          // We don't close — the user almost always wants to publish next.
        },
      });
    } else {
      const patch: ReactionRolePatchInput = {
        title: draft.title,
        body: draft.body,
        useEmbed: draft.useEmbed,
        embedColor: draft.embedColor,
        exclusiveGroup: draft.exclusiveGroup,
        enabled: draft.enabled,
        mappings: draft.mappings,
      };
      update.mutate({ id: mode.rr.id, patch });
    }
  };

  const onPublish = (): void => {
    if (mode.kind !== 'edit') return;
    publish.mutate(mode.rr.id);
  };

  const moveMapping = (idx: number, dir: -1 | 1): void => {
    setDraft((d) => {
      const next = [...d.mappings];
      const target = idx + dir;
      if (target < 0 || target >= next.length) return d;
      const a = next[idx];
      const b = next[target];
      if (!a || !b) return d;
      next[idx] = b;
      next[target] = a;
      // Re-number positions to match visual order.
      return { ...d, mappings: next.map((m, i) => ({ ...m, position: i })) };
    });
  };

  const addMapping = (): void => {
    setDraft((d) => ({
      ...d,
      mappings: [
        ...d.mappings,
        {
          emoji: '',
          isCustomEmoji: false,
          customEmojiName: null,
          roleId: roleOptions[0]?.id ?? '',
          position: d.mappings.length,
        },
      ],
    }));
  };

  const removeMapping = (idx: number): void => {
    setDraft((d) => ({
      ...d,
      mappings: d.mappings.filter((_, i) => i !== idx).map((m, i) => ({ ...m, position: i })),
    }));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-xl border border-zinc-800 bg-zinc-950 shadow-2xl">
        <header className="sticky top-0 flex items-center justify-between border-b border-zinc-800 bg-zinc-950 px-5 py-3">
          <h2 className="text-sm font-semibold">
            {mode.kind === 'create'
              ? t('reactionRoles.editor.titleCreate')
              : t('reactionRoles.editor.titleEdit')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1 text-xs text-zinc-300 hover:bg-zinc-800"
          >
            {t('common.dismiss')}
          </button>
        </header>

        <div className="space-y-5 px-5 py-5">
          <Field label={t('reactionRoles.field.channel')}>
            {mode.kind === 'edit' ? (
              // Channel can't change after create — show as read-only chip
              // so the picker doesn't tease the user with a non-functional
              // input.
              <div className="rounded-md border border-zinc-800 bg-zinc-900/40 px-3 py-1.5 text-sm text-zinc-400">
                #{channelOptions.find((c) => c.id === draft.channelId)?.name ?? draft.channelId}
              </div>
            ) : (
              <ChannelPicker
                guildId={guildId}
                value={draft.channelId}
                onChange={(v) => {
                  setDraft((d) => ({ ...d, channelId: v }));
                }}
                placeholder={t('reactionRoles.field.channelPick')}
              />
            )}
          </Field>

          <Field label={t('reactionRoles.field.title')}>
            <input
              type="text"
              maxLength={256}
              className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-sm"
              value={draft.title}
              onChange={(e) => {
                const v = e.target.value;
                setDraft((d) => ({ ...d, title: v }));
              }}
            />
          </Field>

          <Field label={t('reactionRoles.field.body')}>
            <textarea
              maxLength={4000}
              className="min-h-32 w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 font-mono text-sm"
              value={draft.body}
              onChange={(e) => {
                const v = e.target.value;
                setDraft((d) => ({ ...d, body: v }));
              }}
            />
          </Field>

          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-zinc-700 bg-zinc-900"
                checked={draft.useEmbed}
                onChange={(e) => {
                  const v = e.target.checked;
                  setDraft((d) => ({ ...d, useEmbed: v }));
                }}
              />
              {t('reactionRoles.field.useEmbed')}
            </label>
            {draft.useEmbed ? (
              <label className="flex items-center gap-2 text-sm">
                <span className="text-zinc-400">{t('reactionRoles.field.color')}</span>
                <input
                  type="color"
                  className="h-7 w-12 cursor-pointer rounded border border-zinc-800 bg-zinc-950"
                  value={draft.embedColor ?? '#6366f1'}
                  onChange={(e) => {
                    const v = e.target.value;
                    setDraft((d) => ({ ...d, embedColor: v }));
                  }}
                />
              </label>
            ) : null}
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-zinc-700 bg-zinc-900"
                checked={draft.exclusiveGroup}
                onChange={(e) => {
                  const v = e.target.checked;
                  setDraft((d) => ({ ...d, exclusiveGroup: v }));
                }}
              />
              {t('reactionRoles.field.exclusiveGroup')}
            </label>
          </div>

          <section className="rounded-md border border-zinc-800 bg-zinc-900/40 p-4">
            <header className="mb-3 flex items-center justify-between">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-zinc-400">
                {t('reactionRoles.mappings.title')} ({draft.mappings.length}/20)
              </h3>
              <button
                type="button"
                onClick={addMapping}
                disabled={draft.mappings.length >= 20}
                className="rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1 text-xs text-zinc-300 hover:bg-zinc-800 disabled:opacity-50"
              >
                {t('reactionRoles.mappings.add')}
              </button>
            </header>
            {draft.mappings.length === 0 ? (
              <p className="text-xs text-zinc-500">{t('reactionRoles.mappings.empty')}</p>
            ) : (
              <ul className="space-y-2">
                {draft.mappings.map((m, idx) => (
                  <MappingRow
                    key={`m-${String(idx)}`}
                    mapping={m}
                    roleOptions={roleOptions}
                    emojiOptions={emojiOptions}
                    onChange={(next) => {
                      setDraft((d) => ({
                        ...d,
                        mappings: d.mappings.map((mm, i) => (i === idx ? next : mm)),
                      }));
                    }}
                    onMoveUp={
                      idx > 0
                        ? () => {
                            moveMapping(idx, -1);
                          }
                        : undefined
                    }
                    onMoveDown={
                      idx < draft.mappings.length - 1
                        ? () => {
                            moveMapping(idx, 1);
                          }
                        : undefined
                    }
                    onRemove={() => {
                      removeMapping(idx);
                    }}
                  />
                ))}
              </ul>
            )}
          </section>

          {submitError ? (
            <p className="text-xs text-rose-400">
              {extractErrorMessage(submitError, t('common.error'))}
            </p>
          ) : null}
          {publish.isError ? (
            <p className="text-xs text-rose-400">
              {extractErrorMessage(publish.error, t('common.error'))}
            </p>
          ) : null}
          {publish.isSuccess ? (
            <p className="text-xs text-emerald-400">{t('reactionRoles.published')}</p>
          ) : null}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-sm text-zinc-300 hover:bg-zinc-800"
            >
              {t('reactionRoles.button.cancel')}
            </button>
            <button
              type="button"
              onClick={onSubmit}
              disabled={isPending}
              className="rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-50"
            >
              {isPending ? t('common.loading') : t('reactionRoles.button.save')}
            </button>
            {mode.kind === 'edit' ? (
              <button
                type="button"
                onClick={onPublish}
                disabled={isPending || draft.mappings.length === 0}
                className="rounded-md bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-emerald-500 disabled:opacity-50"
              >
                {publish.isPending ? t('common.loading') : t('reactionRoles.button.publish')}
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

function MappingRow({
  mapping,
  roleOptions,
  emojiOptions,
  onChange,
  onMoveUp,
  onMoveDown,
  onRemove,
}: {
  mapping: ReactionRoleMapping;
  roleOptions: { id: string; name: string; color: string }[];
  emojiOptions: CustomEmoji[];
  onChange: (next: ReactionRoleMapping) => void;
  onMoveUp: (() => void) | undefined;
  onMoveDown: (() => void) | undefined;
  onRemove: () => void;
}) {
  const { t } = useTranslation();
  return (
    <li className="flex items-center gap-2 rounded-md border border-zinc-800 bg-zinc-950 p-2">
      <div className="flex flex-col gap-1">
        <button
          type="button"
          onClick={onMoveUp}
          disabled={!onMoveUp}
          className="rounded border border-zinc-800 bg-zinc-900 px-1 py-0 text-xs text-zinc-400 hover:bg-zinc-800 disabled:opacity-30"
          title={t('reactionRoles.mappings.moveUp')}
        >
          ↑
        </button>
        <button
          type="button"
          onClick={onMoveDown}
          disabled={!onMoveDown}
          className="rounded border border-zinc-800 bg-zinc-900 px-1 py-0 text-xs text-zinc-400 hover:bg-zinc-800 disabled:opacity-30"
          title={t('reactionRoles.mappings.moveDown')}
        >
          ↓
        </button>
      </div>

      <label className="flex items-center gap-1 text-xs text-zinc-400">
        <input
          type="checkbox"
          checked={mapping.isCustomEmoji}
          onChange={(e) => {
            onChange({
              ...mapping,
              isCustomEmoji: e.target.checked,
              emoji: '',
              customEmojiName: null,
            });
          }}
        />
        {t('reactionRoles.mappings.custom')}
      </label>

      {mapping.isCustomEmoji ? (
        <select
          className="w-48 rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1 text-sm"
          value={mapping.emoji}
          onChange={(e) => {
            const id = e.target.value;
            const found = emojiOptions.find((em) => em.id === id);
            onChange({
              ...mapping,
              emoji: id,
              customEmojiName: found?.name ?? mapping.customEmojiName,
            });
          }}
        >
          <option value="">{t('reactionRoles.mappings.emojiPick')}</option>
          {emojiOptions.map((em) => (
            <option key={em.id} value={em.id}>
              :{em.name}:
            </option>
          ))}
        </select>
      ) : (
        <input
          type="text"
          className="w-20 rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1 text-center text-sm"
          value={mapping.emoji}
          maxLength={8}
          placeholder="🔥"
          onChange={(e) => {
            const v = e.target.value;
            onChange({ ...mapping, emoji: v });
          }}
        />
      )}

      <select
        className="flex-1 rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1 text-sm"
        value={mapping.roleId}
        onChange={(e) => {
          const v = e.target.value;
          onChange({ ...mapping, roleId: v });
        }}
      >
        <option value="">{t('reactionRoles.mappings.rolePick')}</option>
        {roleOptions.map((r) => (
          <option key={r.id} value={r.id} style={{ color: r.color }}>
            {r.name}
          </option>
        ))}
      </select>

      <button
        type="button"
        onClick={onRemove}
        className="rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1 text-xs text-rose-300 hover:bg-zinc-800"
      >
        {t('common.remove')}
      </button>
    </li>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string | undefined;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="mb-1 block text-xs text-zinc-400">{label}</label>
      {children}
      {hint ? <p className="mt-1 text-xs text-zinc-500">{hint}</p> : null}
    </div>
  );
}
